import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { ErrorResponse } from "@reflag/shared/api";
import {
  FeatureTargetingVersion,
  UpdateFeatureTargetingBody,
} from "@reflag/shared/featureTargetingAPI";

export function useFeatureTargetingUpdateMutation(
  featureId: string,
  currentVersionIds?: string[],
) {
  const { appId } = useEnvironment();
  const queryClient = useQueryClient();

  return useMutation<
    FeatureTargetingVersion[],
    AxiosError<ErrorResponse>,
    UpdateFeatureTargetingBody
  >({
    mutationFn: (formData) =>
      api
        .patch<"/apps/:appId/features/:featureId/targeting">(
          `/apps/${appId}/features/${featureId}/targeting`,
          formData,
          {
            params: {
              currentVersionIds: currentVersionIds,
            },
          },
        )
        .then((response) => response.data.newVersions),

    onSuccess: (versions) => {
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.single(appId, undefined, featureId),
      });
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.targeting(appId, undefined, featureId),
      });

      for (const version of versions) {
        queryClient.invalidateQueries({
          queryKey: featureQueryKeys.targetingVersions(
            appId,
            version.environment.id,
            featureId,
          ),
        });

        queryClient.invalidateQueries({
          queryKey: featureQueryKeys.singleFeatureRolloutStatus(
            appId,
            version.environment.id,
            featureId,
          ),
        });
      }
    },
  });
}
